import { useState } from 'react';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';

import classNames from 'classnames';

import type { ModalProps } from '@uikit';
import { Modal, ModalCloseButton, ModalContent } from '@uikit';

import type { DeserializedAppData } from '@api';

import { namedErrors } from '@shared/constants';
import { setMagicStoreUrmSource } from '@shared/helpers/url';

import { appQueryKeys } from '@query/app';

import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import BeforeVoteStep from './BeforeVoteStep';
import CastVoteStep from './CastVoteStep';
import type { VoteChoiceValue, VoteStrategyValue } from './useVoteStrategy';
import useVoteStrategy from './useVoteStrategy';
import VoteChoiceStep from './VoteChoiceStep';
import VoteRegisteredStep from './VoteRegisteredStep';

enum VotingStep {
  BeforeVote,
  VoteChoice,
  CastVote,
  Success,
}

export interface VotingModalProps extends Omit<ModalProps, 'children'> {
  strategy: VoteStrategyValue;
  app: DeserializedAppData;
  validationId: string;
  applicationSlug?: string;
}

const VotingModal = ({
  strategy,
  validationId,
  app,
  onOpenChange,
  applicationSlug,
  ...props
}: VotingModalProps) => {
  const [step, setStep] = useState(VotingStep.BeforeVote);
  const [choice, setChoice] = useState<VoteChoiceValue>();
  const [reason, setReason] = useState<string>();
  const { vote, isVoting } = useVoteStrategy(strategy);

  const queryClient = useQueryClient();

  const handleVote = async () => {
    if (!choice) {
      return;
    }

    try {
      await vote(
        {
          appId: app.id,
          appLink: app.attributes.appLink,
          choice,
          reason,
          validationId,
          appName: app.attributes.appName,
        },
        {
          onSuccess: () => {
            setStep(VotingStep.Success);

            if (!applicationSlug) {
              return;
            }

            queryClient.invalidateQueries({
              queryKey: appQueryKeys.appValidation(applicationSlug),
            });

            queryClient.invalidateQueries({
              queryKey: appQueryKeys.appCurrentUserMeta(applicationSlug),
            });
          },
        },
      );
    } catch (error) {
      const errorMessage = getErrorMessage(error, namedErrors.DEFAULT);

      if (errorMessage === 'Account not provided' && onOpenChange) {
        onOpenChange(false);
      }

      toast.error(errorMessage);
    }
  };

  const handleLastStepNext = () => {
    onOpenChange?.(false);
  };

  return (
    <Modal
      {...props}
      onOpenChange={(value) => {
        if (!value) {
          toast.dismiss();
        }

        onOpenChange?.(value);
      }}
    >
      <ModalContent
        className={classNames('relative w-full flex flex-col gap-4 max-w-[560px] p-6 sm:px-12')}
      >
        <ModalCloseButton className="self-end !m-0" />

        {step === VotingStep.BeforeVote && (
          <BeforeVoteStep
            appLink={setMagicStoreUrmSource(app.attributes.link)}
            onNext={() => setStep(VotingStep.VoteChoice)}
          />
        )}

        {step === VotingStep.VoteChoice && (
          <VoteChoiceStep
            choice={choice}
            onChoiceChange={setChoice}
            onNext={() => setStep(VotingStep.CastVote)}
          />
        )}

        {step === VotingStep.CastVote && (
          <CastVoteStep
            reason={reason}
            choice={choice}
            isVoting={isVoting}
            onReasonChange={setReason}
            onBack={() => setStep(VotingStep.VoteChoice)}
            onNext={handleVote}
          />
        )}

        {step === VotingStep.Success && (
          <VoteRegisteredStep
            appId={app.attributes.appLink}
            appName={app.attributes.appName}
            onNext={handleLastStepNext}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

export default VotingModal;
